/* eslint-disable camelcase */
import { calculatorFunctions } from '@H/elasticsearch/calculator-functions'
import { getCalculatorInputs } from '@r/calculators/calculators-selectors'
import { findEntityFieldsBySysId } from '@r/contentful-selectors'
import { getESData } from '@r/elasticsearch/elasticsearch-selectors'
import { getSorts, tableState } from '@r/product-tables/product-table-selectors'
import { Contentful } from '@t/contentful'
import uuidv4 from '@u/guid'
import { contentId } from './contentful'
import datalayerPush from './data-layer'
import { ecommFunctions } from './gtm-ecommerce'
import { getValue } from './product-schema'
import { segmentKeysToInputKeys } from './tracking-utils'

// Looking at how they're generated on uswitch, it's just a uuid made once per page, though they make it server side in their case
const comparisonId = uuidv4()
export function getComparisonId() {
  return comparisonId
}

interface TrackingCallbacks {
  productEvent: ProductEventCallback
  tableEvent: TableEventCallback
}
type ProductEventCallback = (trigger: EventTrigger, product: any, productIndex: number) => void
type TableEventCallback = (trigger: EventTrigger, payload: any) => void

/**
 * Generates two functions for easily tracking product table events:
 *    productEvent(trigger: EventTrigger, product: any, index: number) - to track an event related to a specific product
 *    tableEvent(trigger:EventTrigger, payload: any) - To track a generic table event with a payload
 */
export function setupTrackingCallbacksForTable(
  state: any,
  tableProps: Contentful.ProductTableFields,
): TrackingCallbacks {
  const trackingSchema = getTrackingSchema(state, tableProps)
  const dataId = tableProps.dataset.sys.id
  const tableEvent = (trigger: EventTrigger, payload: any) => {
    const trackingData = {
      table_name: tableProps.name,
    }
    const payloadToUse = { ...payload, trackingData }
    triggerEvent(state, dataId, trackingSchema, trigger, payloadToUse)
  }
  const productEvent = (trigger: EventTrigger, product: any, productIndex: number) => {
    const cart_id = uuidv4()
    const order_id = uuidv4()
    const trackingData = {
      product_position: productIndex + 1,
      cart_id,
      order_id,
      table_name: tableProps.name,
    }
    const payload = { ...product, trackingData }
    triggerEvent(state, dataId, trackingSchema, trigger, payload)
  }
  return {
    productEvent,
    tableEvent,
  }
}

/** Wraps a function with parameters to turn it into an event */
export const wrap = <ParamsType extends Array<any>, ReturnType>(
  fn: (...args: ParamsType) => ReturnType,
  ...args: ParamsType
) => {
  return () => fn(...args)
}

export function getTrackingSchema(state: any, tableProps: any) {
  const { segmentProperties, segmentEvents } = tableProps

  const propertiesComp: GenericJsonModel<SegmentPropertyMappings> = findEntityFieldsBySysId(
    state,
    contentId(segmentProperties),
  )
  const eventsComp: GenericJsonModel<SegmentEventMappings> = findEntityFieldsBySysId(
    state,
    contentId(segmentEvents),
  )
  const properties = propertiesComp.data
  const events = eventsComp.data
  return {
    properties,
    events,
  }
}

function triggerEvent(
  state: any,
  dataId: string,
  trackingPlan: TrackingSchema,
  trigger: EventTrigger,
  payload: any,
) {
  if (!trackingPlan || !trackingPlan.events || !trackingPlan.properties) {
    // This product table isn't setup to use segment, do nothing
    return
  }
  // console.log(`Triggering tracking for ${trigger}`)
  // console.log(`Event payload`, payload)
  const joinId = uuidv4()
  // console.log(`Event joinid: ${joinId}`)
  const eventsToSend = trackingPlan.events.filter((eventMap) => eventMap.event_trigger === trigger)
  // console.log('eventsToSend', eventsToSend)
  // console.log(`Events to send: ${eventsToSend}`)
  eventsToSend.forEach((event) => {
    const payloadToUse = addUserInputsToPayload(state, dataId, payload)
    const processedPayload = generatePayloadForEvent(event, trackingPlan, joinId, payloadToUse)
    const eventContext: EventContext = {
      state,
      eventMap: event,
      trackingPlan,
      joinId,
      originalPayload: payload,
      processedPayload,
    }
    // Choose tracking function depending on where we're sending the event
    const trackingFunction = event.destination === 'gtm' ? sendGTMEvent : sendSegmentEvent
    trackingFunction(eventContext)
  })
}

export interface EventContext {
  state: any
  eventMap: SegmentEventMap
  trackingPlan: TrackingSchema
  joinId: string
  originalPayload: any
  processedPayload: any
}

function sendGTMEvent(ctx: EventContext) {
  const eventFunction = ecommFunctions[ctx.eventMap.event_name]
  if (!eventFunction) {
    // eslint-disable-next-line no-console
    console.warn(`GTM implementation for ${ctx.eventMap.event_name} is missing!`)
  } else {
    return eventFunction(ctx)
  }
}

function sendSegmentEvent(ctx: EventContext) {
  const { eventMap, processedPayload } = ctx
  callNerdalytics(eventMap.event_name, processedPayload)
}

export function trackInteraction(
  action: string,
  category: string,
  label: string,
  org_subvertical: string = 'mortgages',
) {
  // Clear ecommerce before pushing a new event just in case
  datalayerPush({ ecommerce: null })
  callNerdalytics(action, {
    category,
    label,
    org_subvertical,
  })
}
/** Wraps nerdalytics call to automatically wait for GA cookie and add them to the event */
export async function callNerdalytics(
  action: string,
  properties: { [key: string]: any },
  callback: () => {} = () => ({}),
  context?: { [key: string]: any },
) {
  if (typeof window !== 'undefined') {
    // TODO: Figure out how to make sure events aren't accidentally triggered server side
    const nerdalytics = (window as any).nerdalytics
    if (nerdalytics) {
      const t = setTimeout(callback, 1000)
      nerdalytics.track(action, properties, context, () => {
        clearTimeout(t)
        if (callback) {
          callback()
        }
      })
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Nerdalytics isn't defined`)
      callback()
    }
  }
}

/** Inject important user input values into the payload
 * so they can be captured in the event if the tracking plan needs it */
function addUserInputsToPayload(state: any, dataId: string, payload: any) {
  const productTable = tableState(state, dataId)
  // console.log(`Sending event ${eventMap.event_name}`)
  const calculatorData: any = getCalculatorInputs(state, productTable.calculatorId)
  const selectedSort = getSorts(state, dataId).selectedSort
  const sortsInfo: any = {}
  if (selectedSort) {
    sortsInfo.name = selectedSort.label
    sortsInfo.order = selectedSort.order
  }
  const inputs: { [key: string]: any } = {}
  const filters: any[] = []
  let ltv: any = null
  if (calculatorData) {
    for (const i in calculatorData) {
      filters.push({
        filter_name: i,
        filter_value: calculatorData[i],
      })
    }
    // Fill up the inputs object for the segment event
    for (const i in segmentKeysToInputKeys) {
      inputs[i] = calculatorData[segmentKeysToInputKeys[i]]
    }
    if (calculatorData.mortgageAmount && calculatorData.propertyValue) {
      ltv = calculatorFunctions.mortgageLtvNumber(calculatorData, {})
    }
  }
  const es = getESData(state, dataId)
  const pagination: any = {}
  if (es) {
    pagination.max_records_per_view = productTable.maxPerPage
    pagination.offset = productTable.pageOffset

    // this needs to be es[key].hits.total
    pagination.total_records = es.hits.total
  }
  return { ...payload, filters, sortsInfo, pagination, inputs, ltv }
}

/** Generates the final payload that is sent to Segment
 * Uses the property mapping to find the final values for everything and adds the event join id
 */
function generatePayloadForEvent(
  eventMap: SegmentEventMap,
  trackingPlan: TrackingSchema,
  joinId: string,
  payload: any,
): any {
  const properties = lookupProperties(eventMap.properties, trackingPlan.properties, payload)
  const finalPayload = {
    // eslint-disable-next-line camelcase
    event_join_id: joinId,
    ...properties,
  }
  return finalPayload
}

/** For a given segment tracking map, finds all the properties using the property mapping to replace their values */
function lookupProperties(
  properties: SegmentEventProperty[],
  propertiesMapping: SegmentPropertyMappings,
  payload: any,
) {
  const resultProps: any = {}
  properties.forEach((prop) => {
    if (typeof prop === 'string') {
      let mapping = propertiesMapping[prop]
      if (!mapping) {
        // eslint-disable-next-line no-console
        console.warn(
          `Property mapping for ${prop} doesn't exist! You should add it to the segment mapping in contentful`,
        )
        mapping = {
          value: prop,
        }
      }
      let value: any = getValue(mapping, payload)
      // Hardcoded handling of string booleans. Later we will add a more generic system of typing per property
      if (value === 'true') {
        value = true
      } else if (value === 'false') {
        value = false
      }
      resultProps[prop] = value
    } else if (typeof prop === 'object') {
      const key = prop.key
      const operationType = prop.type
      const keyOverride = prop.keyOverride
      // By convention, the payload to use should be inside the payload and have the same key as the prop
      const newPayload = payload[key].map((arrayElement: any) => {
        // We need to pass global tracking data through for all array elements in case it's needed
        return { ...arrayElement, trackingData: payload.trackingData }
      })
      // Used for list events, same process but the payload is an array so we repeat it for each element
      if (operationType === 'product_array') {
        let arrayResult = lookupArrayProperties(prop.properties, propertiesMapping, newPayload)
        if (typeof prop.limit === 'number') {
          arrayResult = arrayResult.slice(0, prop.limit)
        }
        resultProps[keyOverride || key] = arrayResult
      }
      // TODO: Implement array mappings for product list events
    }
  })
  return resultProps
}

// Processes an array element in the tracking plan, doing the same process as a normal element but for each payload value
function lookupArrayProperties(
  properties: SegmentEventProperty[],
  propertiesMapping: SegmentPropertyMappings,
  payload: any[],
): any[] {
  const result: any[] = []
  for (const key in payload) {
    // Inject the index of the element in the tracking data because it might be needed
    const trackingData = {
      ...payload[key].trackingData,
      product_position: parseInt(key, 10) + 1,
    }
    const payloadToLookup = { ...payload[key], trackingData }
    const propsForElement = lookupProperties(properties, propertiesMapping, payloadToLookup)
    result.push(propsForElement)
  }
  return result
}

type EventTrigger = 'product-clickout' | 'product-list-loaded'
// Typing for the new generic reuseable json contentful model
interface GenericJsonModel<T> {
  name: string
  data: T
}

interface SegmentPropertyMappings {
  [key: string]: SegmentPropertyMap
}

// One of the two will be present
interface SegmentPropertyMap {
  value?: string
  valueFrom?: string
}

type SegmentEventMappings = Array<SegmentEventMap>
interface SegmentEventMap {
  // eslint-disable-next-line camelcase
  event_name: string
  // eslint-disable-next-line camelcase
  event_trigger: string
  event_type?: string
  properties: SegmentEventProperty[]
  destination?: string
}

interface SegmentEventObjectProp {
  key: string
  type: string
  limit?: number
  properties: SegmentEventProperty[]
  keyOverride?: string
}
type SegmentEventProperty = string | SegmentEventObjectProp
interface TrackingSchema {
  properties: SegmentPropertyMappings
  events: SegmentEventMappings
}
